import { useEffect, useState } from 'react';
import {
  AGE_CATEGORY_OPTIONS,
  AVAILABILITY_OPTIONS,
  GENDERS,
  SKILL_OPTIONS,
  VOLUNTEERING_LEVELS,
} from '../constants/registration';
import AreaOptions from './AreaOptions';
import './ProfileDetails.css';

const AGE_CATEGORY_LABELS = Object.fromEntries(AGE_CATEGORY_OPTIONS.map((c) => [c.value, c.label]));

/** Ensure a stored value that is no longer in the option list still shows up in the select. */
function withCurrent(options, current) {
  return current && !options.includes(current) ? [current, ...options] : options;
}

const SECTIONS = [
  {
    title: 'Basic information',
    fields: [
      { key: 'name', label: 'Full name' },
      { key: 'caste', label: 'Caste' },
      { key: 'subCaste', label: 'Sub caste' },
      { key: 'phone', label: 'Phone', type: 'tel' },
      { key: 'whatsApp', label: 'WhatsApp', type: 'tel' },
      { key: 'gender', label: 'Gender', options: GENDERS },
      { key: 'birthDate', label: 'Birth date', type: 'date' },
      {
        key: 'ageCategory',
        label: 'Age category',
        options: AGE_CATEGORY_OPTIONS.map((c) => c.value),
        labels: AGE_CATEGORY_LABELS,
      },
    ],
  },
  {
    title: 'Current address',
    fields: [
      { key: 'country', label: 'Country' },
      { key: 'state', label: 'State' },
      { key: 'city', label: 'City' },
      { key: 'area', label: 'Area / locality', areas: true },
      { key: 'street', label: 'Street / house no.', type: 'textarea', wide: true },
      { key: 'pincode', label: 'Pincode', type: 'pincode' },
    ],
  },
  {
    title: 'Permanent address',
    sameAsToggle: true,
    fields: [
      { key: 'permanentCountry', label: 'Country' },
      { key: 'permanentState', label: 'State' },
      { key: 'permanentCity', label: 'City' },
      { key: 'permanentArea', label: 'Area / locality' },
      { key: 'permanentStreet', label: 'Street / house no.', type: 'textarea', wide: true },
      { key: 'permanentPincode', label: 'Pincode', type: 'pincode' },
    ],
  },
  {
    title: 'Volunteering',
    fields: [
      { key: 'volunteerDistrict', label: 'District' },
      { key: 'volunteerCity', label: 'City' },
      { key: 'volunteerTaluka', label: 'Taluka' },
      { key: 'volunteerVillage', label: 'Village' },
      { key: 'volunteerWard', label: 'Ward' },
      {
        key: 'volunteeringCategory',
        label: 'Volunteering category',
        options: AGE_CATEGORY_OPTIONS.map((c) => c.value),
        labels: AGE_CATEGORY_LABELS,
      },
      { key: 'volunteeringLevel', label: 'Volunteering level', multi: VOLUNTEERING_LEVELS, wide: true },
    ],
  },
  {
    title: 'Professional details',
    fields: [
      { key: 'jobBusiness', label: 'Job / business' },
      { key: 'education', label: 'Education' },
      { key: 'availability', label: 'Availability', options: AVAILABILITY_OPTIONS },
      { key: 'referralId', label: 'Reference ID (optional)' },
      { key: 'availabilityDescription', label: 'Availability description', type: 'textarea', wide: true },
      { key: 'previousExperience', label: 'Previous experience', type: 'textarea', wide: true },
      { key: 'skills', label: 'Skills', multi: SKILL_OPTIONS, wide: true },
    ],
  },
];

/** Flatten an API profile into editable form values. */
export function profileToForm(p) {
  const perm = p.permanentAddress || {};
  return {
    name: p.name || '',
    caste: p.caste || '',
    subCaste: p.subCaste || '',
    phone: p.phone || '',
    whatsApp: p.whatsApp || '',
    gender: p.gender || '',
    birthDate: p.birthDate || '',
    ageCategory: p.ageCategory || '',
    street: p.street || '',
    area: p.area || '',
    city: p.city || '',
    state: p.state || '',
    pincode: p.pincode || '',
    country: p.country || '',
    sameAsCurrentAddress: Boolean(p.sameAsCurrentAddress),
    permanentStreet: perm.street || '',
    permanentArea: perm.area || '',
    permanentCity: perm.city || '',
    permanentState: perm.state || '',
    permanentPincode: perm.pincode || '',
    permanentCountry: perm.country || '',
    volunteerDistrict: p.volunteerDistrict || '',
    volunteerCity: p.volunteerCity || '',
    volunteerTaluka: p.volunteerTaluka || '',
    volunteerVillage: p.volunteerVillage || '',
    volunteerWard: p.volunteerWard || '',
    volunteeringCategory: p.volunteeringCategory || '',
    volunteeringLevel: p.volunteeringLevel || [],
    jobBusiness: p.jobBusiness || '',
    education: p.education || '',
    availability: p.availability || '',
    availabilityDescription: p.availabilityDescription || '',
    previousExperience: p.previousExperience || '',
    referralId: p.referralId || '',
    skills: p.skills || [],
  };
}

function displayValue(field, value) {
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (!value) return '—';
  if (field.labels && field.labels[value]) return field.labels[value];
  return value;
}

/**
 * Personal details shown read-only, or as an editable form when `editing` is true.
 * `onSave(values)` should resolve to `{ error, errors }` on failure or nothing on success.
 */
export default function ProfileDetails({ profile, editing, onSave, onCancel }) {
  const [values, setValues] = useState(() => profileToForm(profile));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    setValues(profileToForm(profile));
    setErrors([]);
  }, [profile, editing]);

  const set = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));
  const toggleIn = (key, item) =>
    setValues((prev) => ({
      ...prev,
      [key]: prev[key].includes(item) ? prev[key].filter((x) => x !== item) : [...prev[key], item],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors([]);
    try {
      const result = await onSave(values);
      if (result && (result.error || result.errors)) {
        setErrors(result.errors?.length ? result.errors : [result.error]);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="pd-root">
        {SECTIONS.map((section) => (
          <section key={section.title} className="pd-section">
            <h3 className="pd-section-title">{section.title}</h3>
            {section.sameAsToggle && profile.sameAsCurrentAddress ? (
              <p className="pd-muted">Same as current address</p>
            ) : (
              <dl className="pd-grid">
                {section.fields.map((f) => (
                  <div key={f.key} className={`pd-item${f.wide ? ' wide' : ''}`}>
                    <dt>{f.label}</dt>
                    <dd>{displayValue(f, values[f.key])}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>
        ))}
      </div>
    );
  }

  const renderInput = (f) => {
    const v = values[f.key];
    if (f.multi) {
      return (
        <div className="pd-chips">
          {f.multi.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`pd-chip${v.includes(opt) ? ' on' : ''}`}
              onClick={() => toggleIn(f.key, opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    }
    if (f.areas) {
      return (
        <select className="pd-input" value={v} onChange={(e) => set(f.key, e.target.value)}>
          <option value="">Select…</option>
          <AreaOptions current={v} />
        </select>
      );
    }
    if (f.options) {
      return (
        <select className="pd-input" value={v} onChange={(e) => set(f.key, e.target.value)}>
          <option value="">Select…</option>
          {withCurrent(f.options, v).map((o) => (
            <option key={o} value={o}>
              {f.labels?.[o] || o}
            </option>
          ))}
        </select>
      );
    }
    if (f.type === 'textarea') {
      return (
        <textarea className="pd-input pd-textarea" value={v} onChange={(e) => set(f.key, e.target.value)} />
      );
    }
    if (f.type === 'pincode') {
      return (
        <input
          className="pd-input"
          inputMode="numeric"
          maxLength={6}
          value={v}
          onChange={(e) => set(f.key, e.target.value.replace(/\D/g, '').slice(0, 6))}
        />
      );
    }
    return (
      <input
        className="pd-input"
        type={f.type || 'text'}
        inputMode={f.type === 'tel' ? 'numeric' : undefined}
        value={v}
        onChange={(e) => set(f.key, e.target.value)}
      />
    );
  };

  return (
    <form className="pd-root" onSubmit={submit}>
      {errors.length ? (
        <div className="pd-errors" role="alert">
          <strong>Please fix the following:</strong>
          <ul>
            {errors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {SECTIONS.map((section) => (
        <section key={section.title} className="pd-section">
          <h3 className="pd-section-title">{section.title}</h3>
          {section.sameAsToggle ? (
            <label className="pd-check">
              <input
                type="checkbox"
                checked={values.sameAsCurrentAddress}
                onChange={(e) => set('sameAsCurrentAddress', e.target.checked)}
              />
              Same as current address
            </label>
          ) : null}
          {section.sameAsToggle && values.sameAsCurrentAddress ? null : (
            <div className="pd-grid">
              {section.fields.map((f) => {
                // Chip groups hold buttons, which must not sit inside a <label>.
                const Wrapper = f.multi ? 'div' : 'label';
                return (
                  <Wrapper key={f.key} className={`pd-item${f.wide ? ' wide' : ''}`}>
                    <span className="pd-label">{f.label}</span>
                    {renderInput(f)}
                  </Wrapper>
                );
              })}
            </div>
          )}
        </section>
      ))}

      <div className="pd-actions">
        <button type="button" className="pd-btn ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="pd-btn primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}
